import { Capacitor } from "@capacitor/core";
// Reused, not reimplemented: the throttle contract is identical to the
// stale-build guard's — the first check always runs, then at most one per
// interval, and stamping before the await doubles as the in-flight guard.
import { shouldCheckVersion } from "@router/staleBuildReload";
import {
  isBuildReloadSafe,
  subscribeBuildReloadSafety,
} from "@/services/buildReloadSafety";
import { safeConsole } from "@/utils/safeConsole";
import { getNativeCallState, type NativeCallState } from "./callKit";
import {
  IOS_MINIMUM_BUILD_POLICY_URL,
  type MinimumBuildPolicy,
  parseMinimumBuildPolicy,
  parseNativeBuildNumber,
  requiresUpdate,
} from "./minimumBuildPolicy";

/**
 * Minimum-build update gate for the iOS shell (SD-19983) — the web-safe half.
 *
 * Reads the installed native build once, then asks the deployed web origin for
 * the minimum-supported build at launch and on every foreground. While the
 * installed build is below a VALIDATED minimum the app replaces itself with the
 * blocking "Update required" screen.
 *
 * Four properties this file exists to hold:
 *
 * 1. **It fails open.** No policy, an unreachable origin, a malformed document,
 *    an unreadable build number, a non-iOS platform — all mean "keep running".
 *    The gate only ever acts on data it fully understands.
 * 2. **It is reversible without a store release.** The decision is not latched.
 *    Reads continue while blocked — on every foreground, and on a standing
 *    timer, so an app sitting on the screen releases itself rather than waiting
 *    for a user who has no reason to background it. Only a *validated* read
 *    moves the decision: a failed or malformed one retains the last validated
 *    answer, so a CDN blip can neither block nor unblock anyone.
 * 3. **It never cuts a live call.** Blocking waits on the same
 *    `buildReloadSafety` blockers a stale-build reload waits on (the dock
 *    phone, the HubSpot window surfaces, dialing, an open wrap-up card) AND on
 *    native's own view of CallKit, because a call answered from the lock screen
 *    may not be mirrored into the phone store yet when the WebView wakes up.
 *    Anything short of a confident "no native call" defers. A deferred block
 *    retries on the next foreground, when the final blocker is released, and on
 *    a timer — the last of which is what covers a native call ending while the
 *    app is in the foreground with no JS blocker to release.
 * 4. **`@capacitor/app` stays out of the entry chunk.** The plugin lives behind
 *    the gated `import()` of `./updateGateNative`, like `pushNotifications.ts`
 *    and `callKit.ts` before it.
 *
 * Nothing is persisted on the device, deliberately: a stored threshold would
 * outlive the correction of a wrong one, and recovery has to be a web deploy
 * away rather than a reinstall away.
 */

/**
 * Minimum gap between policy reads, and the period of the standing re-read a
 * blocked app runs. One interval for both: the throttle is what bounds how
 * often the gate asks, so a faster timer would only be throttled back to this.
 */
const UPDATE_GATE_CHECK_MIN_INTERVAL_MS = 60_000;

/** How long a block deferred by a live call waits before trying again. */
export const UPDATE_GATE_DEFERRED_RETRY_MS = 10_000;

const POLICY_FETCH_TIMEOUT_MS = 10_000;

type NativeUpdateGateModule = Pick<
  typeof import("./updateGateNative"),
  "getNativeBuildNumber" | "getAppActive" | "subscribeAppState"
>;

/**
 * Read the deployed policy. Resolves to null when the document was reachable
 * but not a policy, and rejects when nothing was learned at all.
 */
const fetchDeployedPolicy = async (): Promise<MinimumBuildPolicy | null> => {
  // AbortController + setTimeout rather than AbortSignal.timeout: the shell's
  // deployment target is iOS 15 (ios/App/App.xcodeproj), whose WKWebView
  // predates AbortSignal.timeout (Safari 16). There it would throw before the
  // request was made and silently disable the gate on the oldest supported OS.
  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort();
  }, POLICY_FETCH_TIMEOUT_MS);
  let body: string;
  try {
    // Deliberately unauthenticated: a public static asset on the web origin,
    // read before anyone has signed in. No token to carry, so none of the
    // authenticated-client machinery applies.
    const response = await fetch(IOS_MINIMUM_BUILD_POLICY_URL, {
      cache: "no-store",
      signal: controller.signal,
    });
    // A refusal or a 5xx taught us nothing, so it belongs on the silent retry
    // path. A MISSING policy does not arrive here as a 404: the distribution
    // rewrites 404 and 403 to index.html with a 200 (terraform/cds/cds.tf), so
    // an origin that predates the file answers with the SPA shell, which falls
    // out below as unreadable.
    if (!response.ok) throw new Error(`policy HTTP ${response.status}`);
    body = await response.text();
  } finally {
    clearTimeout(timeout);
  }
  try {
    // Validated at the boundary so no caller handles a raw response body.
    return parseMinimumBuildPolicy(JSON.parse(body));
  } catch {
    // Reachable but not JSON — the SPA shell above, or the trailing comma
    // somebody hand-edited in while raising the minimum. Null rather than a
    // throw, so the caller logs it: a syntax error is the likeliest operator
    // mistake there is, and it must not take the silent lane.
    return null;
  }
};

export interface NativeUpdateGate {
  /** Read the build, wire lifecycle listeners, and run the launch check. */
  start: () => void;
  /** Drop every listener and timer; the gate reports nothing after this. */
  stop: () => void;
}

/**
 * The gate's answer about this build. Carries both numbers when it blocks:
 * together they are the difference between "this device is genuinely stale"
 * and "we published a bad minimum", which is the question support is asked
 * during exactly the incident this screen exists for.
 */
export type MinimumBuildRequirement =
  | { required: false }
  | { required: true; installedBuild: number; minimumBuild: number };

export interface NativeUpdateGateOptions {
  /**
   * Called whenever the reported requirement changes — blocking when the app
   * must stop, releasing when a lowered minimum frees it. Never called twice
   * with the same answer.
   */
  onRequirementChange: (requirement: MinimumBuildRequirement) => void;
  // Dependencies are injectable for tests (the unit project runs in node with
  // no fetch, no DOM and no plugins); production callers pass only the callback.
  getPlatform?: () => string;
  loadNative?: () => Promise<NativeUpdateGateModule>;
  /** Null means "reachable but not a policy"; a rejection means "unknown". */
  fetchPolicy?: () => Promise<MinimumBuildPolicy | null>;
  isEnforcementSafe?: () => boolean;
  subscribeEnforcementSafety?: (listener: () => void) => () => void;
  getCallState?: () => Promise<NativeCallState>;
  /** Schedules the gate's one pending timer and returns its cancel. */
  scheduleRetry?: (run: () => void, delayMs: number) => () => void;
  now?: () => number;
  minIntervalMs?: number;
  logger?: Pick<typeof safeConsole, "warn">;
}

export const createNativeUpdateGate = ({
  onRequirementChange,
  getPlatform = () => Capacitor.getPlatform(),
  loadNative = () => import("./updateGateNative"),
  fetchPolicy = fetchDeployedPolicy,
  isEnforcementSafe = isBuildReloadSafe,
  subscribeEnforcementSafety = subscribeBuildReloadSafety,
  getCallState = getNativeCallState,
  scheduleRetry = (run, delayMs) => {
    const timer = setTimeout(run, delayMs);
    return () => {
      clearTimeout(timer);
    };
  },
  now = () => Date.now(),
  minIntervalMs = UPDATE_GATE_CHECK_MIN_INTERVAL_MS,
  logger = safeConsole,
}: NativeUpdateGateOptions): NativeUpdateGate => {
  let stopped = false;
  let installedBuild: number | null = null;
  /** Last VALIDATED answer. A failed or malformed read never changes it. */
  let requiredByPolicy = false;
  /** The minimum that produced that answer, reported alongside the build. */
  let validatedMinimum: number | null = null;
  /** What the consumer was last told, so each change is reported once. */
  let reportedRequired = false;
  let lastCheckedAtMs: number | null = null;
  /** One native call-state probe at a time; see applyRequirement. */
  let probeInFlight = false;
  /**
   * True while the shell is known to be in the foreground. Starts false, and
   * "not yet known" is deliberately the same value as "known to be in the
   * background": both mean do not take the app away. An app can launch
   * straight into the background, and Capacitor drops a transition emitted
   * before anything is listening, so assuming the foreground here would let
   * the gate unmount the router with nobody looking.
   */
  let appActive = false;
  /**
   * Bumped on every lifecycle transition. A native call-state observation is
   * only good for the generation it was taken in: the device can answer a call
   * while the app is away, and the reply that eventually reaches JS describes
   * the moment it was asked, not the moment it arrives.
   */
  let generation = 0;
  let cancelRetry: (() => void) | null = null;
  let unsubscribeSafety: (() => void) | null = null;
  let removeForegroundListener: (() => void) | null = null;

  /**
   * Run gate work fired from a listener or a timer, where there is no caller
   * left to hand a rejection to. Nothing in here is allowed to surface as an
   * unhandled rejection: the gate is a background guard, not the app.
   */
  const runDetached = (work: Promise<void>): void => {
    void work.catch((error) => {
      logger.warn(
        "application-lifecycle",
        "iOS update gate step failed",
        error,
      );
    });
  };

  const clearRetry = (): void => {
    cancelRetry?.();
    cancelRetry = null;
  };

  /**
   * Arm the gate's single timer. Two things need one: a block deferred by a
   * live call, which retries quickly because it is waiting on a call to end,
   * and a block that has landed, which re-reads the policy on the throttle's
   * own interval so a lowered minimum releases the app even if the user never
   * backgrounds it. Whichever is armed, only one is ever outstanding.
   */
  const armRetry = (delayMs: number): void => {
    if (stopped || cancelRetry !== null) return;
    cancelRetry = scheduleRetry(() => {
      cancelRetry = null;
      runDetached(tick());
    }, delayMs);
  };

  /**
   * What the timer does when it fires. A blocked app is waiting on the policy;
   * a deferred one is waiting on a call.
   *
   * Hoisted for the same reason as `applyRequirement`: this trio calls round
   * in a circle, so something has to be reachable before it is written.
   */
  async function tick(): Promise<void> {
    if (!reportedRequired) {
      // Two jobs that must not be able to hold each other up. The deferred
      // case arms its own retry before it can throw, so there is nothing to
      // re-arm here; it re-reads the policy as well because a block deferred
      // by a long call is the one state that never reaches a foreground, and
      // without that, lowering the minimum mid-call would still block the user
      // the moment the call ended. Run concurrently, not in sequence: a wedged
      // native probe would otherwise strand the policy read, and a slow fetch
      // would delay the call-safety retry. The throttle keeps the read to one
      // a minute however often the deferral spins.
      await Promise.allSettled([applyRequirement(), check()]);
      return;
    }
    try {
      await check();
    } finally {
      // Re-armed even if that threw: while blocked, this timer is the only
      // thing that can discover a lowered minimum without the user
      // backgrounding the app, and a gate that stops asking stays blocked.
      if (reportedRequired) armRetry(minIntervalMs);
    }
  }

  /**
   * Bring the reported requirement in line with the last validated decision.
   *
   * Blocking waits for every call host in this document to go idle and for
   * native to say, confidently, that CallKit is not on a call. Releasing never
   * waits: a lowered minimum should reach the user as fast as it reaches the
   * CDN.
   *
   * A hoisted declaration rather than a const: it and `armRetry` call
   * each other, so one of the two has to be reachable before it is written.
   */
  async function applyRequirement(): Promise<void> {
    if (stopped || installedBuild === null) return;
    // Never take the app away while it is in the background. Nobody is looking,
    // the WebView is about to be suspended mid-teardown, and the call state we
    // would act on is the least observable it ever gets. The foreground that
    // follows re-runs this.
    if (!appActive && requiredByPolicy) {
      armRetry(UPDATE_GATE_DEFERRED_RETRY_MS);
      return;
    }
    if (!requiredByPolicy || validatedMinimum === null) {
      clearRetry();
      if (!reportedRequired) return;
      reportedRequired = false;
      onRequirementChange({ required: false });
      return;
    }
    if (reportedRequired) return;
    // A call hosted by this document — dock softphone, HubSpot window, an
    // in-flight dial, an open wrap-up card — defers exactly as it defers a
    // stale-build reload.
    if (!isEnforcementSafe()) {
      armRetry(UPDATE_GATE_DEFERRED_RETRY_MS);
      return;
    }
    // Armed BEFORE the probe, and cleared on the way out. The native bridge
    // dispatches through a serialized executor, so a wedged or never-invoked
    // completion leaves this promise pending for good; arming first means such
    // a probe costs one stalled attempt rather than the retry that would have
    // recovered from it.
    armRetry(UPDATE_GATE_DEFERRED_RETRY_MS);
    // ...which is also why the retry must not issue a second probe while the
    // first is still out. Without this, a wedged bridge would accumulate one
    // pending plugin call and one live continuation every ten seconds for as
    // long as the app stays open.
    if (probeInFlight) return;
    probeInFlight = true;
    const probeGeneration = generation;
    // Native owns CallKit audio and can be on a call the WebView has not
    // mirrored into the phone store yet (answered from the lock screen while
    // JS was suspended), so ask it directly before taking the app away. An
    // unreachable bridge answers "unknown", which is not evidence of an idle
    // line: defer and ask again rather than cut a call that may be live.
    let callState: NativeCallState;
    try {
      callState = await getCallState();
    } finally {
      probeInFlight = false;
    }
    // Re-read the synchronous state after that await: a call can start, or a
    // lowered minimum can land, while native is answering.
    if (stopped || reportedRequired || installedBuild === null) return;
    if (!requiredByPolicy || validatedMinimum === null) {
      // Withdrawn while we asked. Nothing to re-observe for, stale answer or
      // not, so this check comes before the generation fence below.
      clearRetry();
      return;
    }
    // The app went away and came back while native was answering. Whatever it
    // said describes the device before that, and a call answered in the gap is
    // exactly what this is meant to catch — so throw the answer away and ask
    // again. The re-ask is the retry armed before the probe, which is still
    // outstanding and now finds the in-flight guard released; arming here is
    // belt-and-braces for a future ordering change, not the mechanism.
    if (probeGeneration !== generation) {
      armRetry(UPDATE_GATE_DEFERRED_RETRY_MS);
      return;
    }
    if (callState !== "idle" || !isEnforcementSafe()) {
      armRetry(UPDATE_GATE_DEFERRED_RETRY_MS);
      return;
    }
    clearRetry();
    reportedRequired = true;
    onRequirementChange({
      required: true,
      installedBuild,
      minimumBuild: validatedMinimum,
    });
    // The block has landed. From here the timer's job changes: it becomes the
    // standing policy re-read that releases the app if the minimum is lowered
    // while the user sits on the screen without ever backgrounding it.
    armRetry(minIntervalMs);
  }

  const check = async (): Promise<void> => {
    if (stopped || installedBuild === null) return;
    // One clock read for both the test and the stamp, so the throttle cannot
    // be measured against a moment it did not record.
    const startedAtMs = now();
    if (!shouldCheckVersion(lastCheckedAtMs, startedAtMs, minIntervalMs))
      return;
    lastCheckedAtMs = startedAtMs;
    let next: MinimumBuildPolicy | null;
    try {
      next = await fetchPolicy();
    } catch {
      // Offline, timed out, blocked, or no policy deployed yet. The last
      // validated decision stands; the next foreground retries, and a failed
      // attempt costs one interval.
      return;
    }
    if (stopped) return;
    if (next === null) {
      // Reachable but unreadable: an operator mistake that would otherwise
      // change behaviour in total silence. The last validated decision stands.
      logger.warn(
        "application-lifecycle",
        "Ignoring malformed iOS minimum-build policy",
        // Which document was read: the gate can be pointed at a PR preview's
        // copy, and "which one did this build actually read" is the first
        // question anyone asks when the answer looks wrong.
        IOS_MINIMUM_BUILD_POLICY_URL,
      );
      return;
    }
    requiredByPolicy = requiresUpdate(installedBuild, next);
    validatedMinimum = next.minimumBuild;
    await applyRequirement();
  };

  /**
   * Foreground handler.
   *
   * The policy read and the enforcement attempt run concurrently and neither
   * waits on the other: the attempt waits on the native call bridge, and a
   * wedged bridge must not take the one read that can discover a lowered
   * minimum down with it, while a slow read must not hold up a block that has
   * been deferred since the last foreground. `check()` ends by applying
   * whatever it validated, so either path can be the one that acts.
   */
  const onForeground = async (): Promise<void> => {
    await Promise.allSettled([check(), applyRequirement()]);
  };

  const wire = async (): Promise<void> => {
    const native = await loadNative();
    // Listener first, before any other await. Capacitor drops an
    // `appStateChange` emitted while nothing is listening, so every await that
    // runs ahead of this is a window in which the app can go to the background
    // without the gate ever hearing about it.
    const removeListener = await native.subscribeAppState((isActive) => {
      // Every transition invalidates an in-flight observation — of the native
      // call state, and of the activity seed below. The device can answer a
      // call while the app is away, and it can end one.
      generation += 1;
      appActive = isActive;
      if (isActive) runDetached(onForeground());
    });
    if (stopped) {
      // stop() ran while the listener was being registered, so it could not
      // have cleaned this one up.
      removeListener();
      return;
    }
    removeForegroundListener = removeListener;
    // Now seed the activity that the listener can only report changes to.
    // Fenced against the listener: a transition while this was in flight
    // describes a later moment than the snapshot does, so the snapshot loses.
    // A failure leaves `appActive` false, which costs enforcement until the
    // next foreground rather than risking one with nobody looking.
    const seedGeneration = generation;
    try {
      const active = await native.getAppActive();
      if (seedGeneration === generation) appActive = active;
    } catch (error) {
      logger.warn(
        "application-lifecycle",
        "iOS app activity unreadable; update gate waits for a foreground",
        error,
      );
    }
    const reportedBuild = await native.getNativeBuildNumber();
    const build = parseNativeBuildNumber(reportedBuild);
    if (stopped) return;
    if (build === null) {
      // Not a plain integer build (see parseNativeBuildNumber): there is no
      // comparison to make, so the gate stays off for this process. The value
      // itself is the only thing that makes this actionable.
      logger.warn(
        "application-lifecycle",
        "iOS build number is not comparable; update gate disabled",
        reportedBuild,
      );
      return;
    }
    installedBuild = build;
    // The final call blocker releasing is a retry boundary for a block the
    // launch check below defers.
    unsubscribeSafety = subscribeEnforcementSafety(() => {
      runDetached(applyRequirement());
    });
    await check();
  };

  const begin = async (): Promise<void> => {
    try {
      await wire();
    } catch (error) {
      // A missing plugin or a failed chunk load must not break startup; it
      // costs only the gate, which is the fail-open direction.
      logger.warn(
        "application-lifecycle",
        "iOS update gate unavailable",
        error,
      );
    }
  };

  return {
    start: () => {
      // iOS shell only: ordinary web, the PWA, Android and the Chrome
      // extension never read the policy and never block.
      if (getPlatform() !== "ios") return;
      void begin();
    },
    stop: () => {
      stopped = true;
      clearRetry();
      removeForegroundListener?.();
      removeForegroundListener = null;
      unsubscribeSafety?.();
      unsubscribeSafety = null;
    },
  };
};
